import { useEffect, useRef, useState } from 'react';
import { DeviceSearchParams } from '../../hooks/useDeviceSearch';
import { extractErrorMessage } from '../../utils/extractErrorMessage';
import { isAbortError } from '../../api/deviceListReports';
import { fetchAllDeviceRows } from './deviceListCsv';

/**
 * FS-882 — selecting devices on the Device List and handing the selection to
 * the Devices page, which lists a pasted id list in the order given and walks
 * it with Previous/Next (FS-542).
 *
 * The selection is a set of device ids, never row indexes, so it survives
 * paging, sorting and filtering: a device a later filter hides stays selected
 * and is counted as "not shown". It is kept in sessionStorage so a reload, or
 * coming Back from the Devices page, finds it again.
 *
 * FS-889: the stored selection is per account (`deviceListSelection.v1.<id>`),
 * so an account that signs in after another in the same tab never inherits
 * its device ids. The old account-less key is left alone and never read.
 */

/** sessionStorage key for one account's selection; specific to the Device List. */
export const selectionStorageKey = (userId: number): string => `deviceListSelection.v1.${userId}`;

/**
 * Most ids one trip to the Devices page carries. The list rides in the URL
 * (`/devices?q=…`), and the workspace re-encodes it into its own `list=` param,
 * so 500 three- to five-digit ids stay well inside CloudFront's 8 KB URL limit.
 *
 * Also the most "Select all matching" fetches: more could never be opened in
 * one trip, and an unfiltered walk of a 100k fleet would be 500 requests and a
 * sessionStorage copy that outgrows its quota.
 */
export const OPEN_IN_DEVICES_CAP = 500;

/** The account's stored selection, or none when storage is empty, unreadable or blocked. */
export const readStoredSelection = (userId: number): number[] => {
  try {
    const raw = window.sessionStorage.getItem(selectionStorageKey(userId));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((v): v is number => Number.isInteger(v) && v > 0);
  } catch {
    return [];
  }
};

export const writeStoredSelection = (userId: number, ids: number[]): void => {
  try {
    if (ids.length === 0) window.sessionStorage.removeItem(selectionStorageKey(userId));
    else window.sessionStorage.setItem(selectionStorageKey(userId), JSON.stringify(ids));
  } catch {
    // Storage blocked (private window, previews): the selection still works
    // for as long as the page stays open.
  }
};

/** The header checkbox: none, some (mixed) or all of the rows on the page. */
export type HeaderState = 'none' | 'some' | 'all';

export const headerStateOf = (
  selected: ReadonlySet<number>,
  pageIds: readonly number[],
): HeaderState => {
  const n = pageIds.filter((id) => selected.has(id)).length;
  if (n === 0) return 'none';
  return n === pageIds.length ? 'all' : 'some';
};

/** The ids from one row to another, inclusive, in the order shown; null when
 *  either row is not on the page. */
export const rangeBetween = (
  pageIds: readonly number[],
  fromId: number,
  toId: number,
): number[] | null => {
  const a = pageIds.indexOf(fromId);
  const b = pageIds.indexOf(toId);
  if (a === -1 || b === -1) return null;
  return pageIds.slice(Math.min(a, b), Math.max(a, b) + 1);
};

/**
 * The selection in the Device List's order: devices whose place in the current
 * list is known (a page seen under the current search, filters and sort, or the
 * whole list from "Select all matching") by that place; the rest after them, in
 * the order they were selected.
 */
export const orderSelection = (
  selected: Iterable<number>,
  position: ReadonlyMap<number, number>,
): number[] =>
  [...selected]
    .map((id, i) => ({ id, i, p: position.get(id) ?? Number.POSITIVE_INFINITY }))
    // Infinity - Infinity is NaN, which falls through to the selection order.
    .sort((x, y) => x.p - y.p || x.i - y.i)
    .map((e) => e.id);

/**
 * The Devices page listing exactly these ids, in this order. One id gets a
 * trailing comma: the server reads "214," as a list of one, while a bare "214"
 * is a free-text search that also matches serials and paths containing 214.
 */
export const devicesHrefForIds = (ids: readonly number[]): string =>
  `/devices?q=${ids.join(',')}${ids.length === 1 ? ',' : ''}`;

/** The ids of the first OPEN_IN_DEVICES_CAP devices the list's search,
 *  filters and sort produce, in list order (the CSV download's walker, FS-745,
 *  stopped at the cap: three requests at most). `signal` stops the walk. */
export const fetchAllMatchingIds = async (
  params: DeviceSearchParams,
  fetchPage?: Parameters<typeof fetchAllDeviceRows>[1],
  signal?: AbortSignal,
): Promise<number[]> =>
  (await fetchAllDeviceRows(params, fetchPage, OPEN_IN_DEVICES_CAP, signal)).map((r) => r.id);

interface SelectionOptions {
  /** The signed-in account: its selection is stored under its own key. */
  userId: number;
  /** Device ids on the page, in the order shown. */
  pageIds: number[];
  /** Position of the page's first row in the whole list. */
  pageOffset: number;
  /** The search, filters and sort (not the page): positions belong to it. */
  viewKey: string;
  /** The rows on screen answer the current request (not the previous page's
   *  or view's rows still showing while the next ones load). */
  settled: boolean;
}

export function useDeviceSelection({ userId, pageIds, pageOffset, viewKey, settled }: SelectionOptions) {
  const [selected, setSelected] = useState<Set<number>>(() => new Set(readStoredSelection(userId)));
  useEffect(() => writeStoredSelection(userId, [...selected]), [userId, selected]);

  // Shift-click anchor: the last row toggled without Shift, and the state it
  // was given. A range is set to that state.
  const anchor = useRef<{ id: number; checked: boolean } | null>(null);

  // Where each device sits in the current list, learned from every page shown
  // under this view and from "Select all matching". Recorded only once the
  // rows on screen belong to the current request, so a page that is still
  // being replaced never lands at the next page's offsets.
  const positions = useRef({ key: viewKey, map: new Map<number, number>() });
  const pageKey = pageIds.join(',');
  useEffect(() => {
    if (!settled) return;
    if (positions.current.key !== viewKey) positions.current = { key: viewKey, map: new Map() };
    pageIds.forEach((id, i) => positions.current.map.set(id, pageOffset + i));
    // pageKey stands for pageIds.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settled, viewKey, pageKey, pageOffset]);

  const pageSet = new Set(pageIds);
  const headerState = headerStateOf(selected, pageIds);
  const notShown = [...selected].filter((id) => !pageSet.has(id)).length;

  const toggleRow = (id: number, shiftKey: boolean) => {
    const a = anchor.current;
    if (shiftKey && a && a.id !== id) {
      const range = rangeBetween(pageIds, a.id, id);
      if (range) {
        setSelected((prev) => {
          const next = new Set(prev);
          for (const r of range) {
            if (a.checked) next.add(r);
            else next.delete(r);
          }
          return next;
        });
        return;
      }
      // The anchor is not on this page: a plain click.
    }
    const checked = !selected.has(id);
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
    anchor.current = { id, checked };
  };

  /** Unchecked selects every row on the page; checked or mixed clears them.
   *  Devices selected elsewhere are left alone either way. */
  const toggleHeader = () => {
    const selectPage = headerState === 'none';
    setSelected((prev) => {
      const next = new Set(prev);
      for (const id of pageIds) {
        if (selectPage) next.add(id);
        else next.delete(id);
      }
      return next;
    });
    anchor.current = null;
  };

  const [matching, setMatching] = useState<{ key: string; ids: number[] } | null>(null);
  // Keyed to the view it was started from, like the error: a fetch still
  // running for a previous filter does not show as busy in the new one.
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [matchingError, setMatchingError] = useState<{ key: string; message: string } | null>(null);
  // The view as of the latest render, for a fetch that finishes later.
  const viewKeyRef = useRef(viewKey);
  viewKeyRef.current = viewKey;

  const clear = () => {
    setSelected(new Set());
    anchor.current = null;
    setMatching(null);
    setMatchingError(null);
  };

  /** Select the devices the current view matches (up to the cap), fetched in
   *  list order. A result that lands after the search, filters or sort have
   *  changed belongs to a list no longer on screen, and is dropped; so is a
   *  walk the page stopped (FS-889: the account or the opening changed).
   *  `stillWanted` is the page's own word on that, asked right before the
   *  result is applied: the view key alone cannot tell a reopened report
   *  from the one the walk started under. */
  const selectAllMatching = async (fetchIds: () => Promise<number[]>, stillWanted: () => boolean = () => true) => {
    const key = viewKey;
    setBusyKey(key);
    setMatchingError(null);
    try {
      const ids = await fetchIds();
      if (viewKeyRef.current !== key || !stillWanted()) return;
      if (positions.current.key === key) ids.forEach((id, i) => positions.current.map.set(id, i));
      const fetched = new Set(ids);
      setSelected((prev) => new Set([...ids, ...[...prev].filter((id) => !fetched.has(id))]));
      setMatching({ key, ids });
    } catch (err) {
      if (viewKeyRef.current !== key || isAbortError(err) || !stillWanted()) return;
      setMatchingError({ key, message: extractErrorMessage(err) });
    } finally {
      setBusyKey((k) => (k === key ? null : k));
    }
  };

  const allMatchingSelected =
    matching !== null &&
    matching.key === viewKey &&
    matching.ids.length > 0 &&
    matching.ids.every((id) => selected.has(id));

  /** The selection in the Device List's order (see orderSelection). */
  const orderedIds = (): number[] =>
    orderSelection(
      selected,
      positions.current.key === viewKey ? positions.current.map : new Map<number, number>(),
    );

  return {
    selected,
    headerState,
    notShown,
    toggleRow,
    toggleHeader,
    clear,
    orderedIds,
    selectAllMatching,
    matchingBusy: busyKey === viewKey,
    matchingError: matchingError?.key === viewKey ? matchingError.message : null,
    allMatchingSelected,
    allMatchingCount: allMatchingSelected ? matching!.ids.length : 0,
  };
}
